import {
  ActionIcon,
  Anchor,
  AppShell,
  Badge,
  Button,
  type ButtonProps,
  Checkbox,
  createTheme,
  Divider,
  NavLink,
  Paper,
  PasswordInput,
  rem,
  Select,
  MultiSelect,
  Switch,
  Tabs,
  TextInput,
  Textarea,
  ThemeIcon,
  type MantineTheme,
} from '@mantine/core';

declare module '@mantine/core' {
  export interface MantineThemeOther {
    lineHeightHeading: number;
    lineHeightBody: number;
    titleLetterSpacing: string;
    accentFontFamily: string;
    transitionDuration: string;
    headerHeight: string;
    footerHeight: string;
    logoSize: string;
    contentMaxWidth: string;
    pageGradientLight: string;
    pageGradientDark: string;
    pageBgLight: string;
    pageBgDark: string;
    pageBgAccentLight: string;
    pageBgAccentDark: string;
    surfaceColorLight: string;
    surfaceColorDark: string;
    sidebarColorLight: string;
    sidebarColorDark: string;
    headerColorLight: string;
    headerColorDark: string;
    footerColorLight: string;
    footerColorDark: string;
    panelColorLight: string;
    panelColorDark: string;
    panelStrongColorLight: string;
    panelStrongColorDark: string;
    lineColorLight: string;
    lineColorDark: string;
    textColorLight: string;
    textColorDark: string;
    mutedTextColorLight: string;
    mutedTextColorDark: string;
    fieldBackgroundLight: string;
    fieldBackgroundDark: string;
    brandGradient: string;
    brandAccentStart: string;
    brandAccentEnd: string;
    brandButtonShadow: string;
    panelBorderRadius: string;
    controlBorderRadius: string;
    sidebarWidth: string;
    primaryGradient: {
      from: string;
      to: string;
      deg: number;
    };
    /** Полупрозрачный фон формат-бейджа / активной звезды по тону (П.5, П.2).
     *  light-dark(...)-пара. Базово определено для 4 тонов (brand/orange/teal/blue);
     *  green — parity (TD-002, ARCHSPEC-001: green→green #2fb67e, НЕ brand-fallback);
     *  danger/muted/default → brand. */
    fmtColor: { brand: string; orange: string; teal: string; blue: string };
    /** Цвет текста формат-бейджа: base (light) / soft (dark) по тону (П.5).
     *  light-dark(...)-пара. Определено для 4 тонов; green — parity (TD-002). */
    toneSoftColor: { brand: string; orange: string; teal: string; blue: string };
    /** Цвет активной звезды избранного + бордюр важной карточки (evolve-news-feed
     *  §B7 / PC1-015). `brand` — tone, parity `fmtColor`/`toneSoftColor`-формату.
     *  light-dark(...)-пара; SSR-stable канал через theme.other. */
    starColor: { brand: string };
    /** Полупрозрачный брендовый фон — кольцо-подсветка спецзначка «непрочитано»
     *  (evolve §B7 — `--c-brand-soft`). light-dark(...)-пара. */
    brandSoftColor: string;
    /** Полупрозрачный оранжевый фон — активная звезда/бордюр важной категории
     *  (evolve §B7 — `--c-orange-soft`). light-dark(...)-пара. */
    orangeSoftColor: string;
  }
}

const getButtonStyles = (theme: MantineTheme, props: ButtonProps) => {
  const isDisabled = Boolean(props.disabled);
  // Mantine default = filled, но в styles `props.variant` часто `undefined`,
  // если variant не передан явно — иначе disabled уходит в серый дефолт.
  const variant = props.variant ?? 'filled';
  const isFilled = variant === 'filled';
  const isDefault = variant === 'default';
  const isTransparent = variant === 'transparent';
  const isGradient = variant === 'gradient';

  return {
    root: {
    minHeight: props.size === 'lg' ? rem(52) : props.size === 'md' ? rem(46) : undefined,
    borderRadius: theme.other.controlBorderRadius,
    paddingInline: props.size === 'lg' ? theme.spacing.xl : rem(18),
    fontSize: rem(15),
    fontWeight: 700,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
    boxShadow: isDisabled
      ? 'none'
      : isGradient
        ? theme.other.brandButtonShadow
        : theme.shadows.sm,
    borderWidth: isDefault ? rem(2) : undefined,
    background:
      isDisabled && isFilled
        ? 'light-dark(rgba(123, 77, 255, 0.28), rgba(123, 77, 255, 0.32))'
        : isDisabled && isDefault
          ? 'light-dark(rgba(255, 255, 255, 0.72), rgba(38, 28, 64, 0.72))'
          : isDisabled && isTransparent
            ? 'light-dark(rgba(123, 77, 255, 0.05), rgba(167, 139, 250, 0.08))'
            : isDefault
        ? 'light-dark(rgba(255, 255, 255, 0.96), rgba(38, 28, 64, 0.96))'
        : isFilled
          ? theme.other.brandGradient
        : undefined,
    borderColor:
      isDisabled && isDefault
        ? 'light-dark(rgba(123, 77, 255, 0.08), rgba(167, 139, 250, 0.14))'
        : isDefault
        ? 'light-dark(rgba(123, 77, 255, 0.12), rgba(167, 139, 250, 0.18))'
        : undefined,
    color: isDisabled
      ? isFilled
        ? 'rgba(255, 255, 255, 0.76)'
        : 'light-dark(#6e6790, #b7abd7)'
      : isFilled
        ? theme.white
        : undefined,
    opacity: 1,
    cursor: isDisabled ? 'not-allowed' : undefined,
    },
    rootHovered: {
      transform: isDisabled ? 'none' : 'translateY(-2px)',
      boxShadow: isDisabled ? 'none' : theme.shadows.lg,
      borderColor: !isDisabled && isDefault ? 'var(--mantine-primary-color-filled)' : undefined,
      filter: !isDisabled && (isGradient || isFilled) ? 'brightness(1.04)' : undefined,
    },
  };
};

const getPaperStyles = (theme: MantineTheme) => ({
  root: {
    background: 'light-dark(rgba(255, 255, 255, 0.82), rgba(31, 24, 52, 0.84))',
    borderColor: 'light-dark(rgba(123, 77, 255, 0.12), rgba(167, 139, 250, 0.18))',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
    backdropFilter: 'blur(14px)',
  },
  rootHovered: {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows.md,
    borderColor: 'var(--mantine-primary-color-filled)',
  },
});

const getDividerStyles = () => ({
  root: {
    '--divider-color': 'light-dark(rgba(123, 77, 255, 0.12), rgba(167, 139, 250, 0.18))',
  },
  label: {
    color: 'var(--mantine-color-dimmed)',
    fontSize: rem(13),
    fontWeight: 500,
  },
});

const getSwitchStyles = (theme: MantineTheme, props: { checked?: boolean }) => {
  const checked = Boolean(props.checked);
  return {
    root: {
      display: 'flex',
    },
    // Parity Header theme-toggle (ARCHCODE-001): on = brandGradient, off = lineColor (не gray-3).
    track: {
      width: rem(44),
      minWidth: rem(44),
      height: rem(24),
      borderRadius: 999,
      border: 'none',
      transition: 'background 0.2s ease, background-color 0.2s ease',
      ...(checked
        ? {
            backgroundImage: theme.other.brandGradient,
            backgroundColor: 'transparent',
          }
        : {
            backgroundImage: 'none',
            backgroundColor:
              'light-dark(rgba(123, 77, 255, 0.18), rgba(167, 139, 250, 0.28))',
          }),
    },
    thumb: {
      width: rem(18),
      height: rem(18),
      border: 'none',
      backgroundColor: theme.white,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.18)',
      transition: 'transform 0.3s ease',
    },
  };
};

/** Tabs: только плоские props. Hover/active — в styles/main.css (Turbopack
 *  отвергает `&[data-*]` / `&:hover` в theme.ts как React style properties). */
const getTabsStyles = () => ({
  tab: {
    transition: 'color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
  },
});

const getInputStyles = () => ({
  label: {
    marginBottom: rem(8),
    fontSize: rem(14),
    fontWeight: 700,
    color: 'var(--mantine-color-text)',
  },
  input: {
    minHeight: rem(52),
    borderRadius: rem(16),
    border: '1px solid light-dark(rgba(123, 77, 255, 0.12), rgba(167, 139, 250, 0.18))',
    background: 'light-dark(rgba(255, 255, 255, 0.96), rgba(38, 28, 64, 0.96))',
    color: 'var(--mantine-color-text)',
    padding: `${rem(14)} ${rem(16)}`,
    fontSize: rem(15),
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
    '&::placeholder': {
      color: 'light-dark(#6e6790, #b7abd7)',
      opacity: 0.8,
    },
  },
  description: {
    marginTop: rem(8),
    fontSize: rem(13),
    lineHeight: 1.5,
  },
  error: {
    marginTop: rem(8),
    fontSize: rem(13),
    lineHeight: 1.5,
  },
});

const getTextareaStyles = () => ({
  ...getInputStyles(),
  input: {
    ...getInputStyles().input,
    minHeight: rem(120),
  },
});

/**
 * Стили Combobox-based input-полей (Select / MultiSelect).
 *
 * Расширяет {@link getInputStyles} слотами, которые `getInputStyles` не покрывает:
 * выпадающий список (dropdown), опции (option/group/groupLabel), pills выбранных
 * значений в MultiSelect, кнопка «×» очистить всё (clearButton), chevron и search.
 * Без них эти элементы рендерятся дефолтными Mantine-цветами — в тёмной теме
 * чужеродный серый dropdown/pills/clearButton на фиолетовом `panelColorDark`.
 *
 * Фоны берутся из `theme.other` (fieldBackground / panelStrongColor) для единства
 * с закрытым input-полем и панелями. Цвета — через `light-dark(...)`, чтобы
 * работать до монтирования colorScheme (SSR-stable).
 */
const getComboboxInputStyles = () => {
  const base = getInputStyles();
  return {
    ...base,
    // Выпадающий список опций (popover). Фон — как у закрытого input-поля,
    // чтобы dropdown читался как продолжение поля, а не чужеродная плашка.
    dropdown: {
      borderRadius: rem(16),
      border: '1px solid light-dark(rgba(123, 77, 255, 0.12), rgba(167, 139, 250, 0.18))',
      background: 'light-dark(rgba(255, 255, 255, 0.98), rgba(38, 28, 64, 0.98))',
      boxShadow: '0 4px 16px rgba(75, 37, 142, 0.10), 0 2px 6px rgba(0, 0, 0, 0.05)',
    },
    // Опция списка. Базовые стили здесь; фон при selected/hovered задаётся в
    // styles/main.css через attribute-selectors (theme.ts inline-styles валидируются
    // Next.js/Turbopack как React-style-properties и отвергают &[data-*] селекторы).
    option: {
      borderRadius: rem(10),
      color: 'var(--mantine-color-text)',
    },
    // Заголовок группы опций (напр. «Спецрежимы» в «Мои налоги»).
    group: { padding: `${rem(4)} ${rem(8)}` },
    groupLabel: {
      color: 'light-dark(#6e6790, #b7abd7)',
      fontWeight: 700,
      fontSize: rem(12),
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    // Поле поиска внутри dropdown (searchable MultiSelect).
    search: {
      ...base.input,
      minHeight: rem(40),
      borderRadius: rem(10),
    },
    // Pills выбранных значений в MultiSelect (Mantine <Pill>).
    pill: {
      borderRadius: rem(999),
      background: 'light-dark(rgba(123, 77, 255, 0.12), rgba(167, 139, 250, 0.20))',
      color: 'var(--mantine-color-text)',
      border: '1px solid light-dark(rgba(123, 77, 255, 0.18), rgba(167, 139, 250, 0.28))',
    },
    // Кнопка «×» очистить всё (MultiSelect clearable / Select clearable).
    clearButton: {
      color: 'light-dark(#6e6790, #b7abd7)',
      '&:hover': {
        color: 'var(--mantine-color-text)',
        background: 'light-dark(rgba(123, 77, 255, 0.10), rgba(167, 139, 250, 0.16))',
      },
    },
    // Шеврон справа (индикатор dropdown).
    chevron: {
      color: 'light-dark(#6e6790, #b7abd7)',
    },
  };
};

const getCheckboxStyles = (theme: MantineTheme) => ({
  body: {
    alignItems: 'flex-start',
  },
  input: {
    cursor: 'pointer',
    // Контрастный контур невыбранного чекбокса: раньше — lineColor alpha 0.12/0.18
    // (цвета разделителей), в light почти не читался на белом фоне.
    borderWidth: rem(2),
    borderColor: `light-dark(${theme.colors.gray[5]}, ${theme.colors.gray[3]})`,
    marginTop: rem(2),
  },
  label: {
    cursor: 'pointer',
  },
});

const getBadgeStyles = () => ({
  root: {
    width: 'max-content',
    padding: `${rem(6)} ${rem(12)}`,
    borderRadius: rem(999),
    fontSize: rem(11),
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
  },
});

const getNavLinkStyles = () => ({
  root: {
    minWidth: 0,
    padding: `${rem(14)} ${rem(16)}`,
    borderRadius: rem(18),
    border: '1px solid transparent',
    color: 'var(--mantine-color-dimmed)',
    background: 'rgba(255, 255, 255, 0.02)',
    transition: 'transform 0.25s ease, border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease',
  },
  label: {
    fontSize: rem(14),
    fontWeight: 500,
  },
  section: {
    marginInlineEnd: rem(12),
  },
});

const getThemeIconStyles = () => ({
  root: {
    background: 'rgba(255,255,255,0.08)',
    color: 'var(--mantine-color-text)',
    transition: 'background 0.25s ease, color 0.25s ease',
  },
});

export const theme = createTheme({
  primaryColor: 'violet',
  defaultRadius: 'sm',
  fontFamily: 'var(--font-manrope), sans-serif',
  headings: {
    fontFamily: 'var(--font-manrope), sans-serif',
    fontWeight: '800',
    sizes: {
      h1: {
        fontSize: rem(36),
        lineHeight: '1.1',
      },
      h2: {
        fontSize: rem(32),
        lineHeight: '1.15',
      },
      h3: {
        fontSize: rem(24),
        lineHeight: '1.2',
      },
      h4: {
        fontSize: rem(20),
        lineHeight: '1.2',
      },
    },
  },
  spacing: {
    xs: rem(8),
    sm: rem(12),
    md: rem(16),
    lg: rem(24),
    xl: rem(32),
  },
  radius: {
    xs: rem(4),
    sm: rem(8),
    md: rem(12),
    lg: rem(20),
    xl: rem(28),
  },
  shadows: {
    sm: '0 1px 3px rgba(75, 37, 142, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 4px 16px rgba(75, 37, 142, 0.10), 0 2px 6px rgba(0, 0, 0, 0.05)',
    lg: '0 24px 60px rgba(75, 37, 142, 0.14)',
  },
  breakpoints: {
    xs: '30em',
    sm: '48em',
    md: '64em',
    lg: '74em',
    xl: '90em',
  },
  primaryShade: { light: 5, dark: 4 },
  black: '#111827',
  white: '#FFFFFF',
  autoContrast: true,
  components: {
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: 'default',
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        c: 'inherit',
      },
      styles: {
        root: {
          transition: 'color 0.3s ease',
        },
      },
    }),
    Button: Button.extend({
      defaultProps: {
        radius: 'sm',
        variant: 'filled',
      },
      styles: getButtonStyles,
    }),
    Badge: Badge.extend({
      styles: getBadgeStyles,
    }),
    Checkbox: Checkbox.extend({
      styles: getCheckboxStyles,
    }),
    Divider: Divider.extend({
      styles: getDividerStyles,
    }),
    NavLink: NavLink.extend({
      styles: getNavLinkStyles,
    }),
    Paper: Paper.extend({
      defaultProps: {
        radius: 'lg',
        shadow: 'sm',
        withBorder: true,
      },
      styles: getPaperStyles,
    }),
    PasswordInput: PasswordInput.extend({
      styles: getInputStyles,
    }),
    // Select/MultiSelect — parity TextInput/PasswordInput по input-полю, плюс
    // стилизация dropdown/option/pill/clearButton/chevron (которые getInputStyles
    // не покрывает). Без этого выпадающий список, pills выбранных и кнопка «×»
    // очистить всё рендерятся дефолтным Mantine-серым, чужеродным в тёмной теме.
    Select: Select.extend({
      styles: getComboboxInputStyles,
    }),
    MultiSelect: MultiSelect.extend({
      styles: getComboboxInputStyles,
    }),
    Switch: Switch.extend({
      styles: getSwitchStyles,
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        color: 'violet',
      },
      styles: getTabsStyles,
    }),
    TextInput: TextInput.extend({
      styles: getInputStyles,
    }),
    Textarea: Textarea.extend({
      styles: getTextareaStyles,
    }),
    ThemeIcon: ThemeIcon.extend({
      defaultProps: {
        radius: 'md',
        size: 'lg',
        variant: 'transparent',
      },
      styles: getThemeIconStyles,
    }),
    AppShell: AppShell.extend({
      styles: {
        main: {
          minHeight: '100vh',
        },
      },
    }),
  },
  other: {
    lineHeightHeading: 1.12,
    lineHeightBody: 1.7,
    titleLetterSpacing: '0.02em',
    accentFontFamily: 'var(--font-unbounded), sans-serif',
    transitionDuration: '0.3s',
    headerHeight: rem(82),
    footerHeight: rem(56),
    logoSize: rem(42),
    contentMaxWidth: rem(600),
    pageGradientLight: 'radial-gradient(circle at top left, rgba(123, 77, 255, 0.22), transparent 32%), radial-gradient(circle at top right, rgba(255, 143, 61, 0.18), transparent 30%), linear-gradient(135deg, #f7f4ff, #fff8ef)',
    pageGradientDark: 'radial-gradient(circle at top left, rgba(123, 77, 255, 0.22), transparent 32%), radial-gradient(circle at top right, rgba(255, 143, 61, 0.18), transparent 30%), linear-gradient(135deg, #161326, #21182b)',
    pageBgLight: '#f7f4ff',
    pageBgDark: '#161326',
    pageBgAccentLight: '#fff8ef',
    pageBgAccentDark: '#21182b',
    surfaceColorLight: 'rgba(255, 255, 255, 0.82)',
    surfaceColorDark: 'rgba(31, 24, 52, 0.84)',
    sidebarColorLight: 'linear-gradient(180deg, rgba(255, 255, 255, 0.84), rgba(247, 244, 255, 0.96))',
    sidebarColorDark: 'rgba(24, 19, 42, 0.82)',
    headerColorLight: 'rgba(255, 255, 255, 0.08)',
    headerColorDark: 'rgba(255, 255, 255, 0.08)',
    footerColorLight: 'rgba(255, 255, 255, 0.82)',
    footerColorDark: 'rgba(31, 24, 52, 0.84)',
    panelColorLight: 'rgba(255, 255, 255, 0.82)',
    panelColorDark: 'rgba(31, 24, 52, 0.84)',
    panelStrongColorLight: 'rgba(255, 255, 255, 0.96)',
    panelStrongColorDark: 'rgba(38, 28, 64, 0.96)',
    lineColorLight: 'rgba(123, 77, 255, 0.12)',
    lineColorDark: 'rgba(167, 139, 250, 0.18)',
    textColorLight: '#271f44',
    textColorDark: '#f3ecff',
    mutedTextColorLight: '#6e6790',
    mutedTextColorDark: '#b7abd7',
    fieldBackgroundLight: 'rgba(255, 255, 255, 0.96)',
    fieldBackgroundDark: 'rgba(38, 28, 64, 0.96)',
    brandGradient: 'linear-gradient(135deg, #7b4dff, #ff8f3d)',
    brandAccentStart: '#7b4dff',
    brandAccentEnd: '#ff8f3d',
    brandButtonShadow: '0 14px 30px rgba(123, 77, 255, 0.24)',
    panelBorderRadius: rem(28),
    controlBorderRadius: rem(16),
    sidebarWidth: rem(280),
    primaryGradient: {
      from: 'violet.5',
      to: 'violet.7',
      deg: 135,
    },
    // Полупрозрачный фон формат-бейджа / активной звезды по тону (П.5, П.2).
    // light: fmt-* rgba-фон из :root; dark: [data-theme=dark] fmt-* rgba-фон.
    fmtColor: {
      brand: 'light-dark(rgba(123, 77, 255, 0.14), rgba(167, 139, 250, 0.18))',  // --fmt-purple
      orange: 'light-dark(rgba(255, 143, 61, 0.16), rgba(255, 196, 106, 0.20))', // --fmt-orange
      teal:   'light-dark(rgba(20, 184, 166, 0.16), rgba(45, 212, 191, 0.20))',  // --fmt-teal
      blue:   'light-dark(rgba(59, 130, 246, 0.16), rgba(96, 165, 250, 0.20))',  // --fmt-blue
    },
    // Цвет текста формат-бейджа: base (light) / soft (dark) по тону (П.5).
    toneSoftColor: {
      brand:  'light-dark(#7b4dff, #a78bfa)',  // purple base / --purple-soft
      orange: 'light-dark(#ff8f3d, #ffc46a)',  // orange base / --orange-soft
      teal:   'light-dark(#14b8a6, #5eead4)',  // teal base / teal-soft
      blue:   'light-dark(#3b82f6, #93c5fd)',  // blue base / blue-soft
    },
    // evolve-news-feed §B7 / PC1-015: токены «звезда/непрочитано/важное»
    // через SSR-stable theme.other-канал (parity `fmtColor`/`toneSoftColor`).
    // Контракт §B7 фиксирует light/dark-значения (захардкожены от ui-токенов).
    starColor: {
      // --c-star: оранжевый, захардкожен, НЕ зависит от темы (контракт §B7).
      brand: '#ff8f3d',
    },
    brandSoftColor: 'light-dark(rgba(123, 77, 255, 0.14), rgba(167, 139, 250, 0.18))',
    orangeSoftColor: 'light-dark(rgba(255, 143, 61, 0.16), rgba(255, 196, 106, 0.20))',
  },
});
